// SPDX-License-Identifier: AGPL-3.0-or-later
import {
	canUseWindowFocusedHoverControls,
	subscribeWindowHoverControlsChange,
} from '@app/features/ui/utils/WindowFocusInteractionGuard';
import type React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';

type HoverHook = [React.RefCallback<HTMLElement>, boolean];
export const useHover = (delay = 0): HoverHook => {
	const [hovering, setHovering] = useState(false);
	const previousNode = useRef<HTMLElement | null>(null);
	const timeoutId = useRef<NodeJS.Timeout | null>(null);
	const clearHoverTimeout = useCallback(() => {
		if (!timeoutId.current) return;
		clearTimeout(timeoutId.current);
		timeoutId.current = null;
	}, []);
	const handleMouseEnter = useCallback(() => {
		clearHoverTimeout();
		if (!canUseWindowFocusedHoverControls()) {
			setHovering(false);
			return;
		}
		timeoutId.current = setTimeout(() => {
			if (canUseWindowFocusedHoverControls() && previousNode.current?.matches(':hover')) {
				setHovering(true);
			}
		}, delay);
	}, [clearHoverTimeout, delay]);
	const handleMouseLeave = useCallback(() => {
		clearHoverTimeout();
		setHovering(false);
	}, [clearHoverTimeout]);
	const syncHoverWithWindowFocus = useCallback(() => {
		clearHoverTimeout();
		if (!canUseWindowFocusedHoverControls() || !previousNode.current?.matches(':hover')) {
			setHovering(false);
			return;
		}
		timeoutId.current = setTimeout(() => {
			if (canUseWindowFocusedHoverControls() && previousNode.current?.matches(':hover')) {
				setHovering(true);
			}
		}, delay);
	}, [clearHoverTimeout, delay]);
	useEffect(() => {
		const unsubscribe = subscribeWindowHoverControlsChange(syncHoverWithWindowFocus);
		window.addEventListener('blur', syncHoverWithWindowFocus);
		return () => {
			unsubscribe();
			window.removeEventListener('blur', syncHoverWithWindowFocus);
			clearHoverTimeout();
		};
	}, [clearHoverTimeout, syncHoverWithWindowFocus]);
	const customRef = useCallback(
		(node: HTMLElement | null) => {
			if (previousNode.current) {
				previousNode.current.removeEventListener('mouseenter', handleMouseEnter);
				previousNode.current.removeEventListener('mouseleave', handleMouseLeave);
			}
			if (node) {
				node.addEventListener('mouseenter', handleMouseEnter);
				node.addEventListener('mouseleave', handleMouseLeave);
			}
			previousNode.current = node;
		},
		[handleMouseEnter, handleMouseLeave],
	);
	return [customRef, hovering];
};
